"use client";

import { useEffect, useRef } from "react";
import { useStore } from "@/lib/store";
import { createClient } from "@/lib/supabase/client";
import { PersonalNotificationsError, requestPersonalNotifications } from "@/lib/data/personalNotifications";

/** Lightweight verified all-section count, independent of the selected Inbox filter. */
export function InboxUnreadMonitor({ workspaceId, userId, onCount }: { workspaceId: string; userId: string; onCount: (count: number | null) => void }) {
  const callback = useRef(onCount); callback.current = onCount;
  useEffect(() => {
    let alive = true, invalid = false, revision = 0;
    let request: AbortController | null = null;
    callback.current(null);
    const load = async () => {
      if (!alive || invalid) return;
      const generation = ++revision;
      request?.abort(); const controller = new AbortController(); request = controller;
      try {
        const page = await requestPersonalNotifications(workspaceId, userId, { snoozed: "active", signal: controller.signal });
        if (alive && !invalid && !controller.signal.aborted && generation === revision && page) callback.current(page.unreadCount);
      } catch (error) {
        if (alive && !invalid && !controller.signal.aborted && generation === revision && error instanceof PersonalNotificationsError && error.accessDenied) callback.current(null);
      }
    };
    const invalidate = () => { invalid = true; ++revision; request?.abort(); callback.current(null); };
    const { data: listener } = createClient().auth.onAuthStateChange((_event, session) => { if (session?.user?.id !== userId) invalidate(); });
    const unsubscribe = useStore.subscribe((state) => { if (state.activeWorkspaceId !== workspaceId || state.currentUserId !== userId) invalidate(); });
    const wake = () => { if (document.visibilityState === "visible") void load(); };
    void load(); const timer = setInterval(wake, 30_000);
    window.addEventListener("focus", wake); document.addEventListener("visibilitychange", wake);
    window.addEventListener("pauta:inbox-changed", wake);
    return () => { alive = false; ++revision; request?.abort(); clearInterval(timer); listener.subscription.unsubscribe(); unsubscribe(); window.removeEventListener("focus", wake); document.removeEventListener("visibilitychange", wake); window.removeEventListener("pauta:inbox-changed", wake); };
  }, [workspaceId, userId]);
  return null;
}
